import { useSelector } from "react-redux"
import { useMemo, useRef, useCallback } from "react"
import { get } from "lodash"
import { RootState } from "../../reduxStore/store"
import { selectIsCollectionLoaded } from "./model"
import { Content, EnumTableData } from "./schema"
import { useSmartCMSContent } from "./useSmartCMSContent"

/**
 * A lazy collection map loaderthat accumulates content as collections load
 * and persists previously loaded mappings, making it suitable for incremental CMS content loading.
 *
 * @param backendCollectionName collection name in the enumDataTable
 * @param cmsCollectionName collection name in the cms
 * @param fieldName If the values in the enumDataTable are objects, the field name to use as the key
 * @returns Record<string, string> This is a mapping between the backend collection and the display value of the CMS collection.
 * If the value is not found in the CMS collection, the key is used as the display value.
 *
 * Key improvements over useCmsCollectionMap:
 * - Accumulates and persists mappings as content loads incrementally
 * - Only rebuilds map when new content is actually available
 * - Handles partial loading states gracefully
 * - Returns stable references when possible to prevent unnecessary re-renders
 */
export function useLazyCmsCollectionMap(
  backendCollectionName: keyof EnumTableData,
  cmsCollectionName: keyof Content,
  fieldName?: string,
): Record<string, string> {
  const cmsContent = useSmartCMSContent()
  const providerEnumTableData = useSelector(
    (state: RootState) => state.contentManagement.providerEnumData,
  )
  const practiceEnumTableData = useSelector(
    (state: RootState) => state.contentManagement.practiceEnumData,
  )

  const isCollectionLoaded = useSelector(
    selectIsCollectionLoaded(cmsCollectionName),
  )

  // Use refs to persist the accumulated map across renders
  const accumulatedMapRef = useRef<Record<string, string>>({})
  const lastProcessedContentRef = useRef<unknown>(null)
  const lastBackendListRef = useRef<string[]>([])

  // Derive the backend list from enum data
  const backendList = useMemo(() => {
    const combinedTableData = {
      ...providerEnumTableData,
      ...practiceEnumTableData,
    }
    const value = combinedTableData[backendCollectionName]
    if (!fieldName) {
      return Array.isArray(value) ? (value as string[]) : []
    }
    return Array.isArray(value)
      ? (value as { [key: string]: string }[]).map(
          (listItem) => listItem[fieldName],
        )
      : []
  }, [
    backendCollectionName,
    fieldName,
    practiceEnumTableData,
    providerEnumTableData,
  ])

  // Check if we have new content to process
  const currentCmsCollectionContent = cmsContent[cmsCollectionName]
  const hasNewContent =
    currentCmsCollectionContent !== lastProcessedContentRef.current
  const hasNewBackendList =
    JSON.stringify(backendList) !== JSON.stringify(lastBackendListRef.current)

  // Update map when we have new content OR new backend list
  const updateMapIfNeeded = useCallback(() => {
    if (!backendList.length) {
      return accumulatedMapRef.current
    }

    // For lazy loading: only process if collection is loaded
    // For non-lazy loading: process if we have the content
    const shouldProcess = isCollectionLoaded && currentCmsCollectionContent

    if (!shouldProcess) {
      return accumulatedMapRef.current
    }

    // Only rebuild map if we have new content or new backend list
    if (!hasNewContent && !hasNewBackendList) {
      return accumulatedMapRef.current
    }

    // Build new mappings for items in the backend list
    const newMappings: Record<string, string> = {}

    backendList.forEach((key) => {
      const resolvedLabel = get(currentCmsCollectionContent, key)
      // Ensure we always have a string value
      const finalLabel = typeof resolvedLabel === "string" ? resolvedLabel : key

      newMappings[key] = finalLabel
    })

    // Update accumulated map with new mappings (preserving existing ones not in current backend list)
    accumulatedMapRef.current = {
      ...accumulatedMapRef.current,
      ...newMappings,
    }

    // Update our tracking refs
    lastProcessedContentRef.current = currentCmsCollectionContent
    lastBackendListRef.current = [...backendList]

    return accumulatedMapRef.current
  }, [
    backendList,
    currentCmsCollectionContent,
    hasNewContent,
    hasNewBackendList,
    isCollectionLoaded,
  ])

  // Use useMemo to only recalculate when dependencies actually change
  const finalMap = useMemo(() => {
    return updateMapIfNeeded()
  }, [updateMapIfNeeded])

  return finalMap
}
